import React from 'react'
import { cn } from '../../lib/utils'

interface BadgeProps {
  variant?: 'default' | 'success' | 'destructive' | 'secondary' | 'outline' | 'warning' | 'info' | 'purple' | 'dot'
  size?: 'sm' | 'default' | 'lg'
  className?: string
  children: React.ReactNode
}

export function Badge({ 
  variant = 'default', 
  size = 'default',
  className, 
  children 
}: BadgeProps) {
  const baseClasses = 'inline-flex items-center font-semibold transition-colors'
  
  const sizeClasses = {
    sm: 'text-[10px] px-1.5 py-0.5 rounded',
    default: 'text-xs px-2.5 py-0.5 rounded-full',
    lg: 'text-sm px-3 py-1 rounded-full'
  }
  
  const variantClasses = {
    default: 'bg-orange-100 text-orange-700 border border-orange-200',
    success: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    destructive: 'bg-rose-50 text-rose-700 border border-rose-200',
    secondary: 'bg-slate-100 text-slate-700 border border-slate-200',
    outline: 'border border-slate-300 text-slate-700 bg-transparent',
    warning: 'bg-amber-50 text-amber-700 border border-amber-200',
    info: 'bg-sky-50 text-sky-700 border border-sky-200',
    purple: 'bg-purple-100 text-purple-700 border border-purple-200',
    dot: 'bg-transparent text-slate-600 pl-0'
  }

  return (
    <span className={cn(baseClasses, sizeClasses[size], variantClasses[variant], className)}>
      {children}
    </span>
  )
}

// Status badge with dot indicator
interface StatusBadgeProps {
  status: 'online' | 'offline' | 'warning' | 'error' | 'pending' | 'active' | 'inactive'
  label?: string
  className?: string
}

export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  const statusConfig = {
    online: { color: 'bg-emerald-500', text: 'Online', textColor: 'text-emerald-700' },
    active: { color: 'bg-emerald-500', text: 'Active', textColor: 'text-emerald-700' },
    offline: { color: 'bg-slate-400', text: 'Offline', textColor: 'text-slate-600' },
    inactive: { color: 'bg-slate-400', text: 'Inactive', textColor: 'text-slate-600' },
    warning: { color: 'bg-amber-500', text: 'Warning', textColor: 'text-amber-700' },
    pending: { color: 'bg-amber-500', text: 'Pending', textColor: 'text-amber-700' },
    error: { color: 'bg-rose-500', text: 'Error', textColor: 'text-rose-700' }
  }
  
  const config = statusConfig[status]
  
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", config.textColor, className)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", config.color)} />
      {label || config.text}
    </span>
  )
}

// Count badge for displaying numbers
interface CountBadgeProps {
  count: number
  max?: number
  className?: string
}

export function CountBadge({ count, max = 99, className }: CountBadgeProps) {
  const displayCount = count > max ? `${max}+` : count.toString()
  
  return (
    <span className={cn(
      "inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-bold rounded-full bg-orange-600 text-white",
      className
    )}>
      {displayCount}
    </span>
  )
}
